import { useEffect, useRef, useState } from "react";
import * as echarts from "echarts/core";
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from "echarts/components";
import { LineChart } from "echarts/charts";
import { UniversalTransition } from "echarts/features";
import { CanvasRenderer } from "echarts/renderers";
import { RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { io } from "socket.io-client";
import { useParams } from "react-router-dom";

import LiquidCooledDetails from "../LiquidCooledDetails";
import "../../App.css";
import "./index.css";

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent,
  LineChart,
  CanvasRenderer,
  UniversalTransition,
]);

const seriesPalette = {
  "Total DC Power": "#00e5ff",
  "Total AC Power": "#ffe45c",
  Efficiency: "#ff4fd8",
  "Avg Module Temp": "#39ff88",
};

const getSeriesUnit = (seriesName) => {
  if (seriesName === "Total DC Power" || seriesName === "Total AC Power") {
    return " kW";
  }

  if (seriesName === "Efficiency") {
    return " %";
  }

  if (seriesName === "Avg Module Temp") {
    return " degC";
  }

  return "";
};

const toDateTimeLocalValue = (date) => {
  const pad = (value) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getLatestWindow = () => {
  const end = new Date();
  const start = new Date(end.getTime() - 15 * 60 * 1000);

  return {
    start,
    end,
    startInput: toDateTimeLocalValue(start),
    endInput: toDateTimeLocalValue(end),
  };
};

export default function GraphView() {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const liveData = useRef({
    datetime: [],
    total_dc_power: [],
    total_ac_power: [],
    efficiency: [],
    avg_module_temp: [],
  });
  const { station_id, dispensor_id } = useParams();
  const socketRef = useRef(null);
  const rangeEmitTimerRef = useRef(null);
  const [showGoToLatest, setShowGoToLatest] = useState(false);
  const [rangeInputs, setRangeInputs] = useState(() => {
    const latest = getLatestWindow();
    return { start: latest.startInput, end: latest.endInput };
  });
  const [rangeLabel, setRangeLabel] = useState("Latest 15 minutes");
  const [graphMeta, setGraphMeta] = useState({
    points: 0,
    bucket_seconds: 1,
    mode: "latest",
  });
  const [hasData, setHasData] = useState(true);
  const isAutoZoomRef = useRef(false);
  const userInteractedRef = useRef(false);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return undefined;

    const chart = echarts.init(chartDom);
    chartInstance.current = chart;

    const option = {
      backgroundColor: "transparent",
      useUTC: true,
      legend: {
        top: 8,
        right: 8,
        textStyle: { color: "#f4fbff", fontSize: 12, fontWeight: 800 },
        icon: "roundRect",
        itemGap: 16,
      },
      color: Object.values(seriesPalette),
      tooltip: {
        trigger: "item",
        triggerOn: "mousemove|click",
        confine: true,
        backgroundColor: "rgba(2, 13, 30, 0.96)",
        borderColor: "rgba(160, 226, 255, 0.5)",
        borderWidth: 1,
        padding: [12, 14],
        borderRadius: 12,
        textStyle: { color: "#e9fbff" },
        axisPointer: {
          type: "line",
          lineStyle: { color: "rgba(103, 221, 242, 0.46)", width: 1 },
        },
        formatter: (params) => {
          const point = Array.isArray(params) ? params[0] : params;

          if (!point?.value) return "";

          const date = new Date(point.value[0]);
          const dateLabel = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
          const timeLabel = date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          });

          const value = Number(point.value[1]);
          const displayValue = Number.isFinite(value)
            ? value.toFixed(2)
            : point.value[1];

          return `
            <div class="graph-tooltip">
              <div class="graph-tooltip-date">
                ${dateLabel} ${timeLabel}
              </div>
              <div class="graph-tooltip-row">
                <span style="color:${
                  seriesPalette[point.seriesName] || point.color
                };">&#9679;</span>
                <span>${point.seriesName}</span>
                <strong>${displayValue}${getSeriesUnit(
                  point.seriesName,
                )}</strong>
              </div>
            </div>
          `;
        },
      },
      grid: { top: 64, left: 64, right: 64, bottom: 92 },
      xAxis: {
        type: "time",
        boundaryGap: false,
        splitNumber: 8,
        axisLine: {
          lineStyle: { color: "rgba(223, 247, 255, 0.32)" },
        },
        axisTick: {
          alignWithLabel: true,
          length: 5,
          lineStyle: { color: "rgba(223, 247, 255, 0.2)" },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "rgba(223, 247, 255, 0.08)",
            type: "dashed",
          },
        },
        axisLabel: {
          color: "#b9d7e9",
          margin: 10,
          fontSize: 12,
          fontWeight: 700,
          align: "center",
          formatter: (value) => {
            const date = new Date(value);
            const hours = date.getHours().toString().padStart(2, "0");
            const minutes = date.getMinutes().toString().padStart(2, "0");
            return `${hours}:${minutes}`;
          },
        },
      },
      yAxis: [
        {
          type: "value",
          name: "Power (kW)",
          nameTextStyle: { color: "#9dc8dd", fontWeight: 800 },
          axisLabel: { color: "#b9d7e9", fontSize: 11 },
          axisLine: { lineStyle: { color: "rgba(223, 247, 255, 0.2)" } },
          splitLine: {
            lineStyle: { color: "rgba(223, 247, 255, 0.08)", type: "dashed" },
          },
        },
        {
          type: "value",
          name: "Eff / Temp",
          min: 0,
          max: 100,
          nameTextStyle: { color: "#9dc8dd", fontWeight: 800 },
          axisLabel: { color: "#b9d7e9", fontSize: 11 },
          axisLine: { lineStyle: { color: "rgba(223, 247, 255, 0.2)" } },
          splitLine: { show: false },
        },
      ],
      dataZoom: [
        {
          type: "inside",
          xAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseWheel: true,
          moveOnMouseMove: false,
          throttle: 50,
          minValueSpan: 60 * 1000,
        },
        {
          type: "slider",
          xAxisIndex: 0,
          height: 18,
          bottom: 24,
          borderColor: "rgba(103, 221, 242, 0.22)",
          backgroundColor: "rgba(2, 13, 30, 0.5)",
          fillerColor: "rgba(0, 229, 255, 0.28)",
          handleStyle: {
            color: "#00e5ff",
            borderColor: "#f4fbff",
          },
          moveHandleStyle: {
            color: "#ffe45c",
          },
          textStyle: { color: "#9dc8dd" },
          minValueSpan: 60 * 1000,
        },
      ],
      series: [
        {
          name: "Total DC Power",
          type: "line",
          yAxisIndex: 0,
          smooth: true,
          showSymbol: false,
          symbol: "circle",
          symbolSize: 7,
          lineStyle: {
            width: 4,
            color: seriesPalette["Total DC Power"],
            shadowColor: "rgba(0, 229, 255, 0.55)",
            shadowBlur: 10,
          },
          areaStyle: { color: "rgba(0, 229, 255, 0.1)" },
          emphasis: { focus: "series" },
          data: [],
        },
        {
          name: "Total AC Power",
          type: "line",
          yAxisIndex: 0,
          smooth: true,
          showSymbol: false,
          symbol: "circle",
          symbolSize: 7,
          lineStyle: {
            width: 4,
            color: seriesPalette["Total AC Power"],
            shadowColor: "rgba(255, 228, 92, 0.5)",
            shadowBlur: 10,
          },
          areaStyle: { color: "rgba(255, 228, 92, 0.08)" },
          emphasis: { focus: "series" },
          data: [],
        },
        {
          name: "Efficiency",
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          showSymbol: false,
          symbol: "circle",
          symbolSize: 7,
          lineStyle: {
            width: 4,
            type: "dashed",
            color: seriesPalette.Efficiency,
            shadowColor: "rgba(255, 79, 216, 0.48)",
            shadowBlur: 10,
          },
          emphasis: { focus: "series" },
          data: [],
        },
        {
          name: "Avg Module Temp",
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          showSymbol: false,
          symbol: "circle",
          symbolSize: 7,
          lineStyle: {
            width: 4,
            color: seriesPalette["Avg Module Temp"],
            shadowColor: "rgba(57, 255, 136, 0.5)",
            shadowBlur: 10,
          },
          emphasis: { focus: "series" },
          data: [],
        },
      ],
    };

    chart.setOption(option);

    chart.on("dataZoom", (event) => {
      if (isAutoZoomRef.current) return;

      userInteractedRef.current = true;
      setShowGoToLatest(true);

      const zoom = event.batch?.[0] || event;
      if (zoom?.startValue == null || zoom?.endValue == null) return;

      setRangeInputs({
        start: toDateTimeLocalValue(new Date(zoom.startValue)),
        end: toDateTimeLocalValue(new Date(zoom.endValue)),
      });
      setRangeLabel("Custom zoom range");

      clearTimeout(rangeEmitTimerRef.current);
      rangeEmitTimerRef.current = setTimeout(() => {
        socketRef.current?.emit("timeRangeUpdate", {
          station_id,
          dispensor_id,
          startTime: zoom.startValue,
          endTime: zoom.endValue,
        });
      }, 250);
    });

    const handleResize = debounce(() => chart.resize(), 200);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(rangeEmitTimerRef.current);
      chart.dispose();
    };
  }, [station_id, dispensor_id]);

  useEffect(() => {
    if (!station_id || !dispensor_id) return undefined;

    const socket = io(`${process.env.REACT_APP_IP_ADDRESS || ""}/liveAnalysis`, {
      transports: ["websocket","polling"],
      query: { station_id, dispensor_id },
    });

    socketRef.current = socket;

    socket.on("liveAnalysisResponse", (data) => {
      const datetime = (data?.time || []).map((time) =>
        new Date(time).getTime(),
      );
      const meta = data?.meta || {};
      const chart = chartInstance.current;

      if (!chart) return;

      setHasData(datetime.length > 0);
      setGraphMeta({
        points: meta.points ?? datetime.length,
        bucket_seconds: meta.bucket_seconds ?? 1,
        max_points: meta.max_points,
        mode: meta.mode || "range",
      });

      if (meta.start_time && meta.end_time) {
        if (meta.mode === "latest" && !userInteractedRef.current) {
          setRangeInputs({
            start: toDateTimeLocalValue(new Date(meta.start_time)),
            end: toDateTimeLocalValue(new Date(meta.end_time)),
          });
        }
      }

      liveData.current = {
        datetime,
        total_dc_power: data?.total_dc_power || [],
        total_ac_power: data?.total_ac_power || [],
        efficiency: data?.efficiency || [],
        avg_module_temp: data?.avg_module_temp || [],
      };

      const asSeries = (values) =>
        values.map((value, index) => {
          if (value == null) return [datetime[index], null];

          const numeric = Number(value);
          return [datetime[index], Number.isFinite(numeric) ? numeric : null];
        });

      chart.setOption(
        {
          dataZoom:
            meta.mode === "latest" && !userInteractedRef.current
              ? [
                  { start: 0, end: 100 },
                  { start: 0, end: 100 },
                ]
              : undefined,
          series: [
            { data: asSeries(liveData.current.total_dc_power) },
            { data: asSeries(liveData.current.total_ac_power) },
            { data: asSeries(liveData.current.efficiency) },
            { data: asSeries(liveData.current.avg_module_temp) },
          ],
        },
        false,
        true,
      );
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [station_id, dispensor_id]);

  const handleGoToLatest = () => {
    const chart = chartInstance.current;
    const latest = getLatestWindow();

    setRangeInputs({ start: latest.startInput, end: latest.endInput });
    setRangeLabel("Latest 15 minutes");
    userInteractedRef.current = false;
    setShowGoToLatest(false);
    socketRef.current?.emit("timeRangeUpdate", { mode: "latest" });

    if (chart) {
      isAutoZoomRef.current = true;
      chart.dispatchAction({ type: "dataZoom", start: 0, end: 100 });
      isAutoZoomRef.current = false;
    }
  };

  const applySelectedRange = () => {
    const start = new Date(rangeInputs.start).getTime();
    const end = new Date(rangeInputs.end).getTime();

    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
      return;
    }

    userInteractedRef.current = true;
    setShowGoToLatest(true);
    setRangeLabel("Selected date range");
    socketRef.current?.emit("timeRangeUpdate", {
      station_id,
      dispensor_id,
      startTime: start,
      endTime: end,
    });
  };

  const handleChartZoom = (direction) => {
    const chart = chartInstance.current;
    if (!chart) return;

    const zoom = chart.getOption()?.dataZoom?.[0] || {};
    const start = Number.isFinite(zoom.start) ? zoom.start : 0;
    const end = Number.isFinite(zoom.end) ? zoom.end : 100;
    const center = (start + end) / 2;
    const span = Math.max(end - start, 1);

    let nextStart = start;
    let nextEnd = end;

    if (direction === "in") {
      const nextSpan = Math.max(span * 0.68, 2);
      nextStart = Math.max(0, center - nextSpan / 2);
      nextEnd = Math.min(100, center + nextSpan / 2);
    } else if (direction === "out") {
      const nextSpan = Math.min(span * 1.42, 100);
      nextStart = Math.max(0, center - nextSpan / 2);
      nextEnd = Math.min(100, center + nextSpan / 2);

      if (nextEnd - nextStart < nextSpan) {
        if (nextStart === 0) nextEnd = Math.min(100, nextSpan);
        if (nextEnd === 100) nextStart = Math.max(0, 100 - nextSpan);
      }
    } else {
      nextStart = 0;
      nextEnd = 100;
    }

    userInteractedRef.current = direction !== "reset";
    setShowGoToLatest(direction !== "reset");
    if (direction === "reset") {
      setRangeLabel("Full visible range");
    } else {
      setRangeLabel(direction === "in" ? "Zoomed in range" : "Zoomed out range");
    }

    chart.dispatchAction({
      type: "dataZoom",
      start: nextStart,
      end: nextEnd,
    });
  };

  function debounce(func, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  }

  return (
    <div className="graph-view-page">
      <div className="graph-view-content">
        <section className="graph-hero">
          <div>
            <span className="graph-eyebrow">Live power analytics</span>
            <h1>AC/DC Live Analysis</h1>
            <p>
              Station {station_id} / Dispenser {dispensor_id}
            </p>
          </div>

          {showGoToLatest && (
            <button
              type="button"
              onClick={handleGoToLatest}
              className="graph-latest-button"
            >
              Go to Latest
            </button>
          )}
        </section>

        <section className="graph-range-panel">
          <div className="graph-range-fields">
            <label>
              <span>Start date</span>
              <input
                type="datetime-local"
                value={rangeInputs.start}
                onChange={(event) =>
                  setRangeInputs((current) => ({
                    ...current,
                    start: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              <span>End date</span>
              <input
                type="datetime-local"
                value={rangeInputs.end}
                onChange={(event) =>
                  setRangeInputs((current) => ({
                    ...current,
                    end: event.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="graph-range-actions">
            <button type="button" onClick={applySelectedRange}>
              Apply Range
            </button>
            <button type="button" onClick={handleGoToLatest}>
              Last 15 Min
            </button>
          </div>
        </section>

        <section className="graph-card">
          <div className="graph-card__header">
            <div>
              <span>{rangeLabel}</span>
              <h2>Power, efficiency and temperature</h2>
            </div>
            <div className="graph-live-chip">
              <span />
              {graphMeta.points} points
            </div>
          </div>

          <div className="graph-meta-strip">
            <span>
              Bucket: {graphMeta.bucket_seconds || 1}s
              {graphMeta.max_points ? ` / max ${graphMeta.max_points}` : ""}
            </span>
            <span>Mode: {graphMeta.mode === "latest" ? "rolling" : "fixed"}</span>
          </div>

          <div className="graph-chart-wrap">
            {!hasData && (
              <div className="graph-empty-state">
                No graph data found for this selected time range.
              </div>
            )}
            <div className="graph-zoom-controls" aria-label="Chart zoom controls">
              <button
                type="button"
                aria-label="Zoom in"
                title="Zoom in"
                onClick={() => handleChartZoom("in")}
              >
                <ZoomIn size={18} />
              </button>
              <button
                type="button"
                aria-label="Zoom out"
                title="Zoom out"
                onClick={() => handleChartZoom("out")}
              >
                <ZoomOut size={18} />
              </button>
              <button
                type="button"
                aria-label="Reset zoom"
                title="Reset zoom"
                onClick={() => handleChartZoom("reset")}
              >
                <RotateCcw size={17} />
              </button>
            </div>
            <div ref={chartRef} className="graph-chart echarts-container" />
          </div>
        </section>

        <LiquidCooledDetails stationId={station_id} dispensorId={dispensor_id} />
      </div>
    </div>
  );
}
